import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, ImageUserCheck } from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Button } from '@/components/base/buttons/button';
import { verifyInvitationToken, setupPassword, acceptInvitation } from '@/data/api/companies';
import { useTranslation } from 'react-i18next';
import { useMe, switchCompany, getRedirectPath } from '@/hooks/api/use-auth';
import { setAuthToken, apiClient } from '@/lib/api-client';
import { storage } from '@/lib/local-storage';
import { CompanyType } from '@/types/api/admin';

export const VerifyInvitationPage = () => {
    const { t } = useTranslation(['pages:auth', 'common']);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const token = searchParams.get('token');
    const { data: currentUser } = useMe();
    const isLoggedIn = !!currentUser;

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [verificationData, setVerificationData] = useState<{
        email: string;
        company_name: string;
        company_type: string | null;
        valid: boolean;
        user_exists: boolean;
    } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError(t('verifyInvitation.noToken'));
                setIsLoading(false);
                return;
            }

            try {
                const data = await verifyInvitationToken(token);
                
                // If user is logged in, verify email matches
                if (isLoggedIn && currentUser && currentUser.email !== data.email) {
                    setError(t('verifyInvitation.differentUser') || 'This invitation is for a different user');
                    setIsLoading(false);
                    return;
                }
                
                setVerificationData(data);
                setError(null);
            } catch (err: any) {
                setError(err.message || t('verifyInvitation.invalidToken'));
                setVerificationData(null);
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();
    }, [token, t, isLoggedIn, currentUser]);

    const validatePassword = (): boolean => {
        if (password.length < 8) {
            setPasswordError(t('verifyInvitation.passwordTooShort'));
            return false;
        }
        if (password !== passwordConfirm) {
            setPasswordError(t('verifyInvitation.passwordsDoNotMatch'));
            return false;
        }
        setPasswordError(null);
        return true;
    };

    const getRedirectPathForCompany = async (companyType: string | null, companyId?: string): Promise<string> => {
        if (!companyType) {
            return '/auth/login';
        }

        // Carica menu configuration se disponibile
        let menuConfig: Record<string, boolean> | undefined;
        if (companyId) {
            try {
                const configData = await apiClient<{ items: Record<string, boolean> }>(
                    `/menu-configurations/by-company/${companyId}`,
                    { requireAuth: true }
                );
                menuConfig = configData.items;
            } catch (error) {
                // Se il caricamento fallisce, usa comportamento di default
                console.warn('Failed to load menu configuration, using default', error);
            }
        }

        // Crea un oggetto UserInfo minimo per getRedirectPath
        const userInfo = {
            role: currentUser?.role || 'user',
            company_type: companyType as CompanyType,
            company_id: companyId,
        } as any;

        // Usa la funzione getRedirectPath con la menu configuration
        const redirectPath = getRedirectPath(userInfo, menuConfig);
        
        return redirectPath || '/auth/login';
    };

    const handleAcceptInvitation = async () => {
        if (!token) {
            return;
        }

        setIsSubmitting(true);
        try {
            // 1. Accetta l'invito (crea CompanyUser association)
            const result = await acceptInvitation(token);
            
            // 2. Se l'utente è loggato, esegui switch company per cambiare il contesto attivo
            if (isLoggedIn) {
                try {
                    const tokenResponse = await switchCompany(result.company_id);
                    
                    // 3. Aggiorna token nel localStorage
                    setAuthToken(tokenResponse.access_token);
                    storage.setItem("refresh_token", tokenResponse.refresh_token);
                    
                    // 4. Aggiorna cache React Query
                    queryClient.setQueryData(["auth", "me"], tokenResponse.user);
                    queryClient.invalidateQueries({ queryKey: ["user-companies"] });
                } catch (switchError: any) {
                    // Se lo switch fallisce, mostra un warning ma non blocca il flusso
                    // L'invito è già stato accettato, l'utente può fare switch manualmente dopo
                    console.warn('Error switching company after invitation acceptance:', switchError);
                    setError(switchError.message || 'Invitation accepted, but failed to switch company. Please switch manually from the account menu.');
                }
            }
            
            setSuccess(true);
            
            // 5. Redirect alla dashboard corretta
            setTimeout(async () => {
                if (isLoggedIn) {
                    // Redirect to dashboard of inviting company con controllo menu configuration
                    const redirectPath = await getRedirectPathForCompany(result.company_type, result.company_id);
                    navigate(redirectPath);
                } else {
                    navigate('/auth/login', { 
                        state: { message: t('verifyInvitation.successMessage') }
                    });
                }
            }, 2000);
        } catch (err: any) {
            setError(err.message || t('verifyInvitation.acceptError') || 'Error accepting invitation');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validatePassword() || !token) {
            return;
        }

        setIsSubmitting(true);
        try {
            await setupPassword(token, password, passwordConfirm);
            setSuccess(true);
            setTimeout(() => {
                navigate('/auth/login', { 
                    state: { message: t('verifyInvitation.successMessage') }
                });
            }, 2000);
        } catch (err: any) {
            setError(err.message || t('verifyInvitation.setupError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('verifyInvitation.verifying')}</p>
                </div>
            </div>
        );
    }

    if (error && !verificationData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="max-w-md w-full mx-4">
                    <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                        <XCircle className="size-16 text-error mx-auto mb-4" />
                        <h1 className="text-2xl font-semibold text-primary mb-4">
                            {t('verifyInvitation.errorTitle')}
                        </h1>
                        <p className="text-tertiary mb-6">{error}</p>
                        <div className="flex flex-col gap-3">
                            <Link
                                to="/auth/login"
                                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                            >
                                {t('verifyInvitation.goToLogin')}
                            </Link>
                            <Link
                                to="/"
                                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-md font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover"
                            >
                                {t('verifyInvitation.goToHome')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="max-w-md w-full mx-4">
                    <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                        <CheckCircle className="size-16 text-success-600 mx-auto mb-4" />
                        <h1 className="text-2xl font-semibold text-primary mb-4">
                            {t('verifyInvitation.successTitle')}
                        </h1>
                        <p className="text-tertiary mb-6">{t('verifyInvitation.successMessage')}</p>
                        <p className="text-sm text-quaternary">{t('verifyInvitation.redirecting')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // If user exists, show confirmation without password form
    if (verificationData?.user_exists) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="max-w-md w-full mx-4">
                    <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary">
                        <div className="text-center mb-6">
                            <ImageUserCheck className="size-12 text-success-600 mx-auto mb-4" />
                            <h1 className="text-2xl font-semibold text-primary mb-2">
                                {t('verifyInvitation.titleExistingUser')}
                            </h1>
                            <p className="text-tertiary">
                                {t('verifyInvitation.subtitle', { companyName: verificationData?.company_name })}
                            </p>
                            <p className="text-sm text-quaternary mt-2">
                                {t('verifyInvitation.email', { email: verificationData?.email })}
                            </p>
                        </div>

                        <div className="space-y-4">
                            <p className="text-tertiary text-sm">
                                {t('verifyInvitation.existingUserMessage') || 'You already have an account. Click below to accept the invitation and join this company.'}
                            </p>

                            {error && (
                                <div className="rounded-lg bg-error/10 p-3 text-sm text-error">
                                    {error}
                                </div>
                            )}

                            <Button
                                onClick={handleAcceptInvitation}
                                size="md"
                                color="primary"
                                isLoading={isSubmitting}
                                className="w-full"
                            >
                                {t('verifyInvitation.acceptButton') || 'Accept Invitation'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // New user - show password form
    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="max-w-md w-full mx-4">
                <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary">
                    <div className="text-center mb-6">
                        <ImageUserCheck className="size-12 text-success-600 mx-auto mb-4" />
                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('verifyInvitation.title')}
                        </h1>
                        <p className="text-tertiary">
                            {t('verifyInvitation.subtitle', { companyName: verificationData?.company_name })}
                        </p>
                        <p className="text-sm text-quaternary mt-2">
                            {t('verifyInvitation.email', { email: verificationData?.email })}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <Input
                            label={t('verifyInvitation.passwordLabel')}
                            type="password"
                            placeholder={t('verifyInvitation.passwordPlaceholder')}
                            value={password}
                            onChange={setPassword}
                            isRequired
                            size="md"
                            isInvalid={!!passwordError}
                        />

                        <Input
                            label={t('verifyInvitation.passwordConfirmLabel')}
                            type="password"
                            placeholder={t('verifyInvitation.passwordConfirmPlaceholder')}
                            value={passwordConfirm}
                            onChange={setPasswordConfirm}
                            isRequired
                            size="md"
                            isInvalid={!!passwordError}
                        />

                        {error && (
                            <div className="rounded-lg bg-error/10 p-3 text-sm text-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            size="md"
                            color="primary"
                            isLoading={isSubmitting}
                            className="w-full"
                        >
                            {t('verifyInvitation.submitButton')}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

